import React, { useState } from 'react';
import { Box, Button, IconButton, Stack, TextField, Tooltip, Typography, alpha } from '@mui/material';
import { Check, ContentCopy, ForwardToInboxOutlined, LinkOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SearchableSelect from '../../common/SearchableSelect';
import useTimezone from '../../../hooks/useTimezone';
import { useCopyLink } from '../../../hooks/useCopyLink';
import { isEmailAddress } from '../../../utils/emailAddress';
import { fmtClassName } from '../../../utils/pdf/format';
import { admissionInviteService, type AdmissionInvite } from '../../../services/admissions/admissionInviteService';
import type { IdNamePair } from '../../../types/admissions';

/**
 * The walk-in desk's other ending: instead of filling the form in now, send
 * the family their own link to fill it in at home. Fields to send it, what
 * was sent, and the link already out for an applicant.
 */

/** The link, set off on its own line, with a copy button. */
export const LinkBox: React.FC<{ url: string }> = ({ url }) => {
  const copyLink = useCopyLink();
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 0.75, pl: 1.75, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
      <LinkOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
      <Typography sx={{ flex: 1, minWidth: 0, fontFamily: 'monospace', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {url}
      </Typography>
      <Tooltip title="Copy link">
        <IconButton size="small" onClick={() => copyLink(url, 'Form link')} aria-label="Copy link"><ContentCopy fontSize="small" /></IconButton>
      </Tooltip>
    </Stack>
  );
};

interface FieldsProps {
  who: string;
  email: string;
  classId: string;
  emailOn: boolean;
  classOptions: IdNamePair[];
  disabled?: boolean;
  onEmail: (v: string) => void;
  onClass: (v: string) => void;
}

/** Where to send the link, and the class if the desk already knows it. */
export const FormLinkFields: React.FC<FieldsProps> = ({ who, email, classId, emailOn, classOptions, disabled, onEmail, onClass }) => {
  const invalid = !!email.trim() && !isEmailAddress(email);
  return (
    <Box sx={{ maxWidth: 460, mx: 'auto', pt: { sm: 4 } }}>
      <Typography variant="h6" fontWeight={700}>Send the form link</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        {who}’s family fills in the rest of the form at home, through their own link. The CNICs you checked are already on it.
      </Typography>
      <Stack spacing={2.5}>
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={e => onEmail(e.target.value)}
          error={invalid}
          helperText={invalid ? 'Enter a valid email address.'
            : emailOn ? 'The link is emailed here. Leave it empty to share the link yourself.'
              : 'Form link emails are off in Email setup, so you’ll share the link yourself. The email is kept on the form.'}
          disabled={disabled}
          autoFocus
          fullWidth
        />
        <SearchableSelect
          label="Class"
          value={classId}
          onChange={onClass}
          options={[{ value: '', label: 'Let the family choose' }, ...classOptions.map(c => ({ value: c._id, label: fmtClassName(c.name) }))]}
          fullWidth
          minWidth={0}
          disabled={disabled}
        />
      </Stack>
    </Box>
  );
};

/** After sending: where it went, and the link to share another way. */
export const FormLinkSent: React.FC<{ invite: AdmissionInvite & { emailed: boolean } }> = ({ invite }) => (
  <Box sx={{ maxWidth: 460, mx: 'auto', pt: { sm: 5 }, textAlign: 'center' }}>
    <Box sx={(t) => ({
      width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: alpha(t.palette.success.main, 0.12), color: 'success.main', boxShadow: `0 0 0 8px ${alpha(t.palette.success.main, 0.06)}`,
    })}>
      <Check sx={{ fontSize: 30 }} />
    </Box>
    <Typography variant="h6" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
      {invite.emailed ? `Form link sent to ${invite.email}` : 'Form link ready'}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 3 }}>
      {invite.emailed
        ? 'They can fill it in whenever it suits them. You can also copy the link and send it on WhatsApp.'
        : 'Copy the link and send it to the family, on WhatsApp for example.'}
      {' '}It waits under Form links in Applications until they submit.
    </Typography>
    <Box sx={{ textAlign: 'left' }}><LinkBox url={invite.link} /></Box>
  </Box>
);

/** A link already out for this applicant: when and where it went, to copy or send again. */
export const OpenInviteNotice: React.FC<{ invite: AdmissionInvite; emailOn: boolean; onResent: (next: AdmissionInvite) => void }> = ({ invite, emailOn, onResent }) => {
  const { formatDateTime } = useTimezone();
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      const next = await admissionInviteService.resend(invite._id);
      enqueueSnackbar(`Form link sent to ${next.email}`, { variant: 'success' });
      onResent(next);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Couldn’t send the link', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={(t) => ({ p: 1.75, borderRadius: 2, bgcolor: alpha(t.palette.primary.main, 0.05), border: `1px solid ${alpha(t.palette.primary.main, 0.2)}` })}>
      <Stack direction="row" spacing={1.5}>
        <ForwardToInboxOutlined sx={{ color: 'primary.main', fontSize: 20, mt: 0.1 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={600}>A form link is already out for this applicant</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, overflowWrap: 'anywhere' }}>
            {invite.emailedAt && invite.email ? `Emailed to ${invite.email} on ${formatDateTime(invite.emailedAt)}.` : `Created ${formatDateTime(invite.createdAt)}.`}
            {' '}If you fill the form in now instead, the link closes.
          </Typography>
          <LinkBox url={invite.link} />
          {emailOn && invite.email && (
            <Button size="small" onClick={resend} disabled={busy} sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}>
              {busy ? 'Sending…' : 'Email it again'}
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
